import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
	Controls,
	MiniMap,
	Handle,
	Position,
	addEdge,
	Edge,
	Connection,
	BezierEdge,
	EdgeProps,
    ReactFlowInstance,
} from 'reactflow';
import dagre from 'dagre';
import { NeonEdge } from './neon-edge';
import 'reactflow/dist/style.css';
import './tree-graph-chart.scss';

interface NodeColor {
	id: string;
	color: string;
	borderColor: string;
}
type ChartData = [string, string, string][];
interface CustomNodeProps {
	data: {
		key:string,
		label: string;
		color: string;
		id: string;
		/* Only set on the authentication node. Distinguishes a liveness capture
		   measured below the bar (a review item, which pulses) from one never
		   measured at all (a gap in the record, which does not). */
		livenessVerdict?: string;
	};
}

interface ChartProps {
	isSessionModal: boolean;
	isSessionList?: boolean;
	accreditationTier?: string;
	/** tier per design node id -- wins over `accreditationTier` for that node */
	accreditationTiers?: Record<string, string>;
	/** id of the node that is currently running — drawn as its own state */
	runningNodeId?: string;
	/**
	 * The node an applicant reached and abandoned — a NEW state, not a restyle.
	 *
	 * Until now a stopped step rendered as `pending`, identical to every
	 * downstream step nobody ever reached, so the graph could not distinguish
	 * "the journey died here" from "never got this far". Those are different
	 * facts and demand different responses, so the caller derives the frontier
	 * and passes it in the same way `runningNodeId` is.
	 */
	stoppedNodeId?: string;
	/** How long it has been stalled, for the chip: `17d`. */
	stoppedForDays?: number;
	isInvite: boolean;
	isStepper: boolean;
	onClick: (id: string) => void;
	nodeColor: NodeColor[];
	chartData: ChartData;
	details: any;
	overlayTemplateIds:string[];
}

export const ReactFlowCustomChart: FC<ChartProps> = ({
	isSessionModal,
	isSessionList,
	accreditationTier,
	accreditationTiers,
	runningNodeId,
	stoppedNodeId,
	stoppedForDays,
	isInvite,
	isStepper,
	onClick,
	nodeColor,
	chartData,
	details,
	overlayTemplateIds
}) => {
 
	/**
 * Display-only names for the flow overview. The label on a node comes from the
 * session's OWN design (`design.nodes[].label`), so it cannot be renamed in a
 * constant without rewriting saved flows — it is mapped at render instead.
 */
const OVERVIEW_LABEL: {[key: string]: string} = {
	'KYB Verify Business': 'KYB',
	'KYB (Know your business)': 'KYB',
	'KYC (Know your customer)': 'KYC',
	'Liveness & Biometric': 'Liveness',
	'Individual or Business Questionnaire.': 'Questionnaire',
	'Individual or Business Questionnaire': 'Questionnaire',
	'Document Review': 'Doc Review',
	'Document Revie.': 'Doc Review',
	'Entity/KYB Document Requirements': 'KYB Documents',
	'Accreditation Verification': 'Accreditation',
};

/**
 * What a node will actually measure once rendered: the concept's padding
 * (28 + 30) + 1.5px borders + `[ ` `] ` bracket margins (6px x4), plus the
 * label at 17px Fira Code. Monospace, so the advance is a constant ratio.
 */
const measureNodeWidth = (node: any, compact = false): number => {
	const raw = node?.data?.label ?? '';
	const shown = OVERVIEW_LABEL[raw] ?? raw;
	// Fira Code is monospace, so the advance is a constant ratio of the size.
	// A session ROW uses the same design at a smaller size: at 17px the eight
	// steps measure ~1700px against a ~1070px column, and fitView would zoom
	// the labels to nothing. Compact lands the same flow near 1:1.
	const SIZE = compact ? 12 : 17;
	const PAD = compact ? 30 : 58;
	const BRK = compact ? 16 : 24;
	return Math.ceil(PAD + 3 + BRK + (String(shown).length + 2) * SIZE * 0.6);
};

/** sessionActionStatus (constant.tsx) inverted: fill colour -> what it means. */
const FLOW_STATE: {[key: string]: string} = {
	'#33b87a': 'completed',
	'#e5ae40': 'processing',
	'#d32222': 'rejected',
	'#f55353': 'failed',
	'#7c8398': 'pending',
	'#8c97b8': 'pending',
};

	const CustomNode = useCallback(({ data }: CustomNodeProps) => {
		const truncateLabel = (label: string) => {
			if (!label || typeof label !== 'string') return '';
			// __simplici_neon_flow__: the session modal's nodes are 168px wide, not
			// 105px, so the 14-character cut that fits the ribbon segments would
			// leave "Liveness & Bio..." in a box with room to spare. Longer names
			// still get a cut, just a later one.
			// The concept never truncates: a node is as wide as its name. Only the
			// fixed-width ribbon segments elsewhere still need a cut.
			if (isSessionModal) return OVERVIEW_LABEL[label] ?? label;
			if (label.length <= 10) return label;
			return label.length === 14 ? label : `${label.slice(0, 14).trim()}...`;
		};
		return (
			<div
				style={{ position: 'relative', display: 'inline-block' }}
				className="node_tooltip_hover"
			>
				{/* configure This */}
				<div
					style={{
						...((isStepper && data?.color === '#FFFFFF'
							? {
									visibility: 'visible',
									backgroundColor: 'blue',
									color: 'white',
									bottom: '110%',
									left: '50%',
							  }
							: {
									visibility: 'hidden',
							  }) as React.CSSProperties),
						transition: 'opacity 0.2s ease-in-out',
						textAlign: 'center',
						padding: '5px 10px',
						borderRadius: '4px',
						boxShadow: '0px 0px 5px 0px rgba(0,0,0,0.53)',
						fontSize: '10px',
						position: 'absolute',
						transform: 'translateX(-50%)',
						whiteSpace: 'nowrap',
						cursor: 'pointer',
					}}
				>
					Configuring this
				</div>
				{/* onClick */}
				<div
					style={{
						...((isInvite &&
						(data.color === '#8c97b8' || data.color === '#33b87a') &&
						(data.key === 'signAgreementVerification' ||
							data.key === 'proofVerification' ||
							data.key === 'fundInvestmentVerification')
							? {
									visibility: 'visible',
									backgroundColor: 'blue',
									color: 'white',
									bottom: '110%',
									left: '50%',
							  }
							: {
									visibility: 'hidden',
							  }) as React.CSSProperties),
						transition: 'opacity 0.2s ease-in-out',
						textAlign: 'center',
						padding: '5px 10px',
						borderRadius: '4px',
						boxShadow: '0px 0px 5px 0px rgba(0,0,0,0.53)',
						fontSize: '10px',
						position: 'absolute',
						zIndex: 10,
						transform: 'translateX(-50%)',
						whiteSpace: 'nowrap',
						cursor: 'pointer',
					}}
					onClick={() => onClick(data.id)}
				>
					{overlayTemplateIds?.includes(data.id) &&
					data.key === 'signAgreementVerification' &&
					isInvite
						? 'Recipients Details'
						: isInvite &&
						  data.color === '#8c97b8' &&
						  (data.key === 'signAgreementVerification' ||
								data.key === 'proofVerification')
						? 'Upload'
						: isInvite &&
						  data.color === '#33b87a' &&
						  (data.key === 'signAgreementVerification' ||
								data.key === 'proofVerification')
						? 'Re Upload'
						: isInvite &&
						  data.color === '#8c97b8' &&
						  data.key === 'fundInvestmentVerification'
						? 'Configure'
						: data.label}
				</div>
				{/* tooltip */}
				<div
					className="node_tooltip"
					style={{
						...((isInvite &&
						(data.color === '#8c97b8' || data.color === '#33b87a') &&
						(data.key === 'signAgreementVerification' ||
							data.key === 'proofVerification' ||
							data.key === 'fundInvestmentVerification')
							? {
									bottom: '-80%',
							  }
							: {
									bottom: '100%',
							  }) as React.CSSProperties),
					}}
				>
					{data.label}
				</div>
				{/* BorderDiv mainDiv */}
				<div
					style={{
						...((isStepper && data?.color === '#FFFFFF') ||
						(isInvite && !overlayTemplateIds?.includes(data.id)&&
							data.color === '#8c97b8' &&
							(data.key === 'signAgreementVerification' ||
								data.key === 'proofVerification' ||
								data.key === 'fundInvestmentVerification'))
							? {
									position: 'relative',
									// __simplici_stepper_label_clipping__: authentication was 71px while every
									// other step got 107px -- the narrowest chevron holding the longest
									// label ("Liveness & Biometric"), which the inner overflow:hidden
									// then cut mid-word to "Liveness & B" with no ellipsis.
									width: '107px',
									height: `${30 + 2}px`,
									background: 'blue',
									clipPath:
										'polygon(93% 0%, 100% 50%, 93% 100%, 0% 100%, 7px 50%, 0% 0%)',
							  }
							: {
									...(isSessionModal
										? {width: 'auto', height: isSessionList ? '34px' : '58px'}
										: {width: '105px', height: `${30}px`}), // __simplici_stepper_label_clipping__
							  }),
					}}
				>
					{/* MainDiv */}
					<div
						style={{
							// __simplici_neon_flow__: the concept's node is a 168x52 chip;
							// the 105x30 ribbon segment stays everywhere else.
							// the concept gives .node NO width — padding + nowrap size it,
							// so [ Form ] is 149px and [ Accreditation ] is 285px
							...(isSessionModal
								? {
										width: 'auto',
										height: isSessionList ? '34px' : '58px',
										whiteSpace: 'nowrap' as const,
								  }
								: {width: '105px', height: `${30}px`}), // __simplici_stepper_label_clipping__
							background:
								isInvite &&
								data.color === '#8c97b8' && !overlayTemplateIds?.includes(data.id) &&
								(data.key === 'signAgreementVerification' ||
									data.key === 'proofVerification' ||
									data.key === 'fundInvestmentVerification')
									? '#FFFFFF'
									: data?.color || '#8c97b8',
							clipPath: isSessionModal
								? // the concept's shape: straight left, pointed right
								  'polygon(0 0, calc(100% - 18px) 0, 100% 50%, calc(100% - 18px) 100%, 0 100%)'
								: 'polygon(93% 0%, 100% 50%, 93% 100%, 0% 100%, 7px 50%, 0% 0%)',
							overflow: 'hidden',
							whiteSpace: 'nowrap',
							// __simplici_neon_flow__: absolute takes the chevron OUT OF FLOW,
							// so its wrapper — and therefore .react-flow__node — measures 0
							// wide. react-flow will not draw an edge between zero-width
							// nodes: 8 nodes, 0 edges. In flow, the node reports its real
							// width and the wires attach.
							position: isSessionModal ? 'relative' : 'absolute',
							...(isStepper || isInvite
								? { top: `${1}px`, left: `${1}px` }
								: {}),
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
						}}
						// __simplici_neon_flow__: the fill, the clip-path and the label
						// colour are all INLINE here, so a stylesheet can only reskin this
						// with !important — and it cannot read which state the colour
						// means. The state is published as a data attribute instead, and
						// the session modal's own scss (complex-step-view.scss) draws from
						// it. Nothing else reads the attribute, so the stepper and invite
						// renderings of this component are untouched.
						data-flow-state={
							// "running" is not a colour the backend sends — it is derived
							// from the session having moved recently, so it is passed in
							// and wins over whatever the fill would otherwise mean.
							runningNodeId && data?.id === runningNodeId
								? 'running'
								: // `stopped` outranks the fill for the same reason `running`
								  // does: the colour the backend sends says "pending", which
								  // is true but not the useful truth.
								  stoppedNodeId && data?.id === stoppedNodeId
								  ? 'stopped'
								  : FLOW_STATE[data?.color ?? ''] ?? 'pending'
						}
						/* WHICH step this node is, so a stylesheet can treat one
						   differently without guessing from its colour. Added for the
						   liveness node: its amber is a standing flag, not a queue
						   item, so it must not pulse — see sessions-deck.scss. */
						data-step={data?.key ?? ''}
						data-liveness={data?.livenessVerdict ?? ''}
						onClick={() => onClick(data.id)}
					>
						{/* label */}
						<div
							style={{
								fontSize: '10px',
								...((isStepper && data?.color === '#FFFFFF') ||
								(isInvite && !overlayTemplateIds?.includes(data.id) &&
									data.color === '#8c97b8' &&
									(data.key === 'signAgreementVerification' ||
										data.key === 'proofVerification' ||
										data.key === 'fundInvestmentVerification'))
									? { color: 'blue' }
									: { color: 'white' }),
								position: 'absolute',
								left: 9,
								right: 10,
							}}
						>
							{isSessionModal ? (
								<>
									<span className="nbrk">[</span>
									<span className="nlabel">{truncateLabel(data.label)}</span>
									<span className="nbrk">]</span>
								</>
							) : (
								truncateLabel(data.label)
							)}
						</div>
					</div>
				</div>

				{/* Hung off the WRAPPER, not the chevron: the chevron is clipped by
					its clip-path, which would cut this label off. Bottom-right, so it
					reads as a qualifier on the step rather than another step. */}
				{/* The stall chip. Sibling of the chevron for the same reason
				    `.ntier` is: the chevron's clip-path would cut it off. Renders
				    only on the stopped node, and only when we know how long —
				    "stalled" with no duration is a worse label than none, since
				    the duration is the thing a triager acts on. */}
				{stoppedNodeId && data?.id === stoppedNodeId && !!stoppedForDays && (
					<span className="nstall">stalled · {stoppedForDays}d</span>
				)}

				{isSessionModal && /accredit/i.test(data?.label ?? '') && (() => {
					// This node's own level first; the flow-level value only for a
					// node nobody could resolve (two accreditation steps at different
					// levels is exactly the case a single value gets wrong).
					const tier = accreditationTiers?.[data?.id] ?? accreditationTier;
					return tier ? (
						<span className="ntier" data-tier={tier}>
							{tier}
						</span>
					) : null;
				})()}

				<Handle type="source" position={Position.Right} id="right" />
				<Handle type="source" position={Position.Top} id="top" />
				<Handle type="source" position={Position.Bottom} id="bottom" />
				<Handle type="target" position={Position.Left} id="left" />
			</div>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInvite, isStepper, onClick, overlayTemplateIds, isSessionModal, isSessionList, accreditationTier, accreditationTiers, runningNodeId, stoppedNodeId, stoppedForDays]);

	const CustomEdge: FC<EdgeProps> = useCallback(
		props =>
			// __simplici_neon_flow__: the overview draws the concept's own wire —
			// its buildPath/arrowHeadPath and the travelling pulse (neon-edge.tsx).
			// BezierEdge picks its own control points, so the curve never matched.
			isSessionModal ? (
				<NeonEdge {...props} />
			) : (
				<BezierEdge
					{...props}
					style={{ stroke: 'gray', strokeWidth: 1 }}
					markerEnd="url(#arrowhead)"
				/>
			),
		[isSessionModal]
	);
	  

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const edgeTypes = useMemo(() => ({ custom: CustomEdge }), []);
	

	const getColorById = useCallback(
		(id: string) => nodeColor.find(item => item?.id === id)?.color,
		[nodeColor]
	);

	/* Looked up the same way the colour is. Only the authentication node carries
	   one; it lets the stylesheet tell a liveness capture MEASURED below the bar
	   (a review item, which pulses) from one never measured at all (a gap in the
	   record, which must not). */
	const getLivenessById = useCallback(
		(id: string) =>
			(nodeColor.find(item => item?.id === id) as any)?.livenessVerdict,
		[nodeColor]
	);

	const getInitialNodes = useCallback(
		(node: any) => {
			return (
				isSessionModal || isInvite || isStepper ? node : node.slice(0, 10)
			).map((item: any) => ({
				id: item.id,
				type: 'custom',
				data: {
					label: item.label,
					color: getColorById(item.id),
					id: item.id,
					key: item.key,
					livenessVerdict: getLivenessById(item.id),
				},
			}));
		},
		[isSessionModal, isInvite, isStepper, getColorById, getLivenessById]
	);

	const initialNodes = useMemo(() => getInitialNodes(details), [
		details,
		getInitialNodes,
	  ]);

	const initialEdges: Edge[] = useMemo(() => {
		const edges =
			(isSessionModal || isInvite || isStepper)&& initialNodes.length>1  ? chartData : chartData.slice(1);
		return edges.map((edge, index: number) => ({
			id: `e${index + 1}`,
			source: edge[0],
			target: edge[1],
			animated: true,
			type: 'custom',
		}));
	}, [isSessionModal, isInvite, isStepper, initialNodes.length, chartData]);

	const isHaveChildNodes = useCallback((initialEdges: Edge[]) => {
		const sourceCount = initialEdges.reduce((acc:  { [key: string]: number }, edge: Edge) => {
			acc[edge.source] = (acc[edge.source] || 0) + 1;
			return acc;
		}, {});
		return Object.values(sourceCount).some((count) => count > 1);
	}, []);

	const getLayoutedElements = useCallback(
		(nodes: any, edges: Edge[], direction = 'LR') => {
			const dagreGraph = new dagre.graphlib.Graph();
			dagreGraph.setDefaultEdgeLabel(() => ({}));
			// ranksep IS the gap between steps once the widths above are real
			// A session ROW is the same diagram in one lane, so it wants the gap
			// tight enough that fitView does not have to zoom the labels away.
			dagreGraph.setGraph(
				isSessionModal
					? {
							rankdir: direction,
							// the concept leaves ~51px between nodes; a wire needs room to
							// be a wire rather than a dash between two touching boxes
							ranksep: isSessionList ? 48 : 64,
							nodesep: isSessionList ? 22 : 40,
					  }
					: { rankdir: direction }
			);


			nodes.forEach((node: any) => {
				// __simplici_neon_flow__: 74 is NARROWER than the 105px node, which is
				// what makes the chevrons overlap into one continuous ribbon. The neon
				// concept draws separate nodes joined by a visible wire, so the session
				// modal gets room for one. Stepper and invite keep the ribbon.
				//
				// dagre spaces node CENTRES, so a fixed width against content-sized
				// nodes leaves UNEVEN gaps — a short [ KYC ] ends up further from its
				// neighbour than a long [ KYB Documents ]. Measuring each node makes
				// ranksep the actual gap.
				dagreGraph.setNode(node.id, {
					width: isSessionModal ? measureNodeWidth(node, isSessionList) : 74,
					height: isSessionModal ? (isSessionList ? 34 : 58) : 55,
				});
			})
			
			edges.forEach((edge: Edge) => {
				dagreGraph.setEdge(edge.source, edge.target);
			});

			dagre.layout(dagreGraph);

			nodes.forEach((node: any) => {
				const nodeWithPosition = dagreGraph.node(node.id);
				// the hardcoded -50 assumed a 100px node; centre on the real width
				node.position = {
					x: nodeWithPosition.x - nodeWithPosition.width / 2,
					y: nodeWithPosition.y - nodeWithPosition.height / 2,
				};
				node.draggable = false;
			});

			return { nodes, edges };
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);
	const layoutedElements = useMemo(() => {
		return getLayoutedElements(initialNodes, initialEdges);
	  }, [initialNodes, initialEdges, getLayoutedElements]);
	  
	  const { edges: layoutedEdges } = layoutedElements;
	  
	const [edges, setEdges] = useState(layoutedEdges);

	const onConnect = useCallback(
		(params: Edge | Connection) =>
			setEdges((eds: Edge[]) => addEdge(params, eds)),
		[]
	);

	/**
	 * fitView measures the CONTAINER, and in a table cell that container has not
	 * settled at onInit — the column is still resolving. The first fit was
	 * computed against a wider box than the lane ends up with, which is why the
	 * diagram overflowed both ends of the cell rather than shrinking into it.
	 * Re-fit whenever the lane actually changes size.
	 */
	const rfInstance = useRef<ReactFlowInstance | null>(null);
	const laneRef = useRef<HTMLDivElement | null>(null);

	/* FIT, THEN PIN THE LEFT EDGE — and every re-fit has to do both.
	 *
	 * This used to live inside onInit only, and the ResizeObserver below called
	 * a bare `fitView`. fitView CENTRES what it fits, so the lane settling to
	 * its final width — which it always does, a table cell resolves after the
	 * first paint — silently re-centred a diagram that onInit had just aligned.
	 * The chains ended up starting at a different x in every row, which is
	 * exactly the thing the alignment was written to prevent.
	 *
	 * Shared so there is ONE definition of "fitted and left-aligned"; a second
	 * caller that fits without pinning is the whole bug. */
	const fitLane = useCallback(
		(instance?: ReactFlowInstance | null) => {
			const rf = instance ?? rfInstance.current;
			if (!rf) return;
			rf.fitView({ padding: 0.02, maxZoom: 1 });
			if (!isSessionList) return;
			const nodes = rf.getNodes();
			if (!nodes.length) return;
			const { zoom, y } = rf.getViewport();
			const minX = Math.min(...nodes.map(nd => nd.position?.x ?? 0));
			const INSET = 8;
			rf.setViewport({ x: INSET - minX * zoom, y, zoom });
		},
		[isSessionList]
	);

	useEffect(() => {
		if (!(isSessionModal || isInvite)) return undefined;
		const el = laneRef.current;
		if (!el || typeof ResizeObserver === 'undefined') return undefined;
		let frame = 0;
		const ro = new ResizeObserver(() => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => fitLane());
		});
		ro.observe(el);
		return () => {
			cancelAnimationFrame(frame);
			ro.disconnect();
		};
	}, [isSessionModal, isInvite, fitLane]);

	const onInit = useCallback(
		(instance: ReactFlowInstance) => {
			rfInstance.current = instance;
			if (isSessionModal || isInvite) {
				// fitView measures the nodes it is fitting, and at onInit they have
				// not been laid out yet — the fit was computed from zero-width nodes,
				// which is why the row's diagram sat off-centre and clipped at both
				// ends. Deferred past layout, then once more after the fonts settle:
				// the labels are the node's width, so a late font swap changes it.
				// maxZoom 1: fitView scales UP as happily as down, and a three-step
				// flow in a wide lane was being blown up to twice the size of an
				// eight-step one. Never bigger than drawn; smaller only as needed.
				/* Three passes for the same reason fitView needed them: the nodes
				   have no width until they are laid out, and the labels ARE the
				   width, so a late font swap moves the left edge again. */
				fitLane(instance);
				requestAnimationFrame(() => fitLane(instance));
				setTimeout(() => fitLane(instance), 200);
			} else {
				instance.setViewport(
					isStepper ? { x: 50, y: 50, zoom: 1 } : { x: 100, y: 200, zoom: 1 }
				);
			}
		},
		[isSessionModal, isInvite, isStepper, fitLane]
	);
	

	return (
		<ReactFlow
			ref={laneRef as any}
			nodes={initialNodes}
			edges={edges}
			onConnect={onConnect}
			nodeTypes={nodeTypes}
			edgeTypes={edgeTypes}
			fitView
			fitViewOptions={{ padding: 0.02, maxZoom: 1 }}
			onInit={onInit}
			// The MODAL is where a flow is actually explored, so it pans and zooms.
			// A row lane is a picture of a flow and stays locked (below) — the two
			// were sharing one set of gestures, which meant the modal could not be
			// zoomed either.
			// The MODAL is where a flow is actually explored, so it pans and zooms.
			// A row lane is a picture of a flow and stays locked (below) — the two
			// were sharing one set of gestures, so the modal could not be zoomed
			// either. Scroll PANS and pinch/double-click ZOOMS: with panOnScroll
			// also on, wheel-to-zoom would fight it for the same gesture.
			zoomOnScroll={false}
			zoomOnPinch={isSessionModal && !isSessionList}
			zoomOnDoubleClick={isSessionModal && !isSessionList}
			// A row lane is a PICTURE of a flow, not a canvas. Left pannable, a
			// stray drag or a wheel over the row slides the diagram out of frame
			// and there is no way to put it back — and `panOnScroll` swallowed the
			// wheel, so the page would not scroll while the cursor was over a lane.
			// `preventScrolling={false}` hands the wheel back to the page.
			panOnDrag={!isSessionList}
			panOnScroll={!isSessionList}
			preventScrolling={isSessionList ? false : undefined}
			elementsSelectable={!isSessionList}
			nodesDraggable={false}
			className={
				isSessionModal || isInvite || isStepper
					? 'modal_is_open'
					: isHaveChildNodes(initialEdges)
					? 'havechild_node_chart'
					: 'nochild_node_chart'
			}
		>
			<svg>
				<defs>
					<marker
						id="arrowhead"
						markerWidth="15"
						markerHeight="20"
						refX="10"
						refY="10"
						orient="auto"
					>
						<polygon
							points="10 0, 15 10, 10 20, 8 20, 12 10, 8 0"
							fill="gray"
						/>
					</marker>
				</defs>
			</svg>
			{isSessionModal || isInvite ? (
				<>
					{/* The concept's canvas is the diagram and nothing else — no zoom
						controls, no minimap. Gated in the component rather than hidden in
						css: `.conditional-flow-steps .react-flow__controls { display: none
						!important }` matched the element and still lost to something in
						react-flow's own sheet, and a rule that is in the document, matches,
						and does not win is not worth carrying. */}
					{/* The modal gets zoom controls back — pinch alone is not a
						discoverable affordance on a trackpad-less machine. The row lanes
						and the stepper stay bare. */}
					{isSessionModal && !isSessionList && <Controls showInteractive={false} />}
					{!isSessionModal && <Controls />}
					{!isSessionModal && <MiniMap />}
				</>
			) : (
				<></>
			)}
		</ReactFlow>
	);
};
